import { PlayCircleFilled } from '@ant-design/icons';
import { OmniaVideo, OmniaVideoItem } from '@omniafishing/core';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import articleStyles from '../../components/article/article_body.less';
import { LoadingState } from '../../constants/loading_state';
import { useListAttributionUuid } from '../../hooks/use_list_attribution_uuid';
import { useResponsive } from '../../hooks/use_responsive';
import { useUser } from '../../hooks/use_user';
import { apiV1 } from '../../lib/api';
import { scrollToElementTop } from '../../lib/scroll';
import { userIsOmniaVideoAffiliateMatch } from '../../lib/user';
import { WebAnalytics, WebAnalyticsEventAreas } from '../../lib/web_analytics';
import { InventoryModalActions } from '../../redux/inventory_modal';
import { RoutePaths } from '../../routes';
import { ContentWrapper } from '../content_wrapper/content_wrapper';
import { OmniaVideoComponent } from '../omnia_video/omnia_video';
import { PRODUCTS_NAV_HEIGHT_APPROX } from '../products_nav/products_nav';
import { PublishedDates } from '../published_dates/published_dates';
import { VideoItemProduct } from '../video_items/video_item_product';
import { VideoItemProductFamily } from '../video_items/video_item_product_family';
import { LakeBanner } from './lake_banner';
import { LivestreamFaqs } from './livestream_faqs';
import { LivestreamLakeBlurb } from './livestream_lake_blurb';
import { MobileVideoLiveStreamReload } from './mobile_video_livestream_reload';
import styles from './omnia_video_page_container.less';
import { StreamHasNotStarted } from './stream_has_not_started';
import { StreamIsOver } from './stream_is_over';
import { useOmniaVideoAddToCart, webAnalyticsVideoItemClick } from './use_omnia_video_add_to_cart';
import { useOmniaVideoOpenProductFamilyModal } from './use_omnia_video_open_product_family_modal';

interface OmniaVideoPageProps {
  omnia_video: OmniaVideo;
  omnia_video_items: OmniaVideoItem[];
}

export const getIsOver = (event_end: string, livestream: boolean) => {
  const now = dayjs();
  const eventEndDayjs = dayjs(event_end);
  return livestream && now.isAfter(eventEndDayjs);
};

export const OmniaVideoPage = (props: OmniaVideoPageProps) => {
  const { omnia_video, omnia_video_items } = props;
  const [omniaVideoItems, setOmniaVideoItems] = useState(omnia_video_items);
  const {
    brands,
    event_end,
    live,
    livestream,
    publication_date,
    slug,
    summary,
    title,
    transcript,
    updated_at,
    waterbodies,
  } = omnia_video;

  const { user, isAdmin } = useUser();
  const isAdminOrAffiliateMatch = isAdmin || userIsOmniaVideoAffiliateMatch(user, omnia_video);

  const standardOmniaVideo = !livestream;
  const productItems = omniaVideoItems.filter(
    (vi) => vi.item_type === 'Product' || vi.item_type === 'ProductFamily'
  );

  const { omniaVideoAddToCart } = useOmniaVideoAddToCart();
  const { omniaVideoOpenProductFamilyModal } = useOmniaVideoOpenProductFamilyModal();
  const { isMobile } = useResponsive();
  const dispatch = useDispatch();
  const [currentVideoItemId, setCurrentVideoItemId] = useState(null as number);
  const [timeToSeek, setTimeToSeek] = useState(null as number);
  const list_uuid = useListAttributionUuid();
  const videoScrollRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    let interval: number;
    if (live) {
      interval = window.setInterval(() => {
        apiV1.omniaVideoFetch(slug).then((res) => {
          setOmniaVideoItems(res.data.data.omnia_video_items.reverse());
        });
      }, 5000);
    }
    return () => {
      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, [live]);

  const isOver = getIsOver(event_end, livestream);

  const Title = (
    <h1 className={styles.title}>
      {title}{' '}
      {live && (
        <span className={styles.liveBadge}>
          <PlayCircleFilled />
          Live
        </span>
      )}{' '}
      {isAdminOrAffiliateMatch && <Link to={`${RoutePaths.OMNIA_VIDEOS}/${slug}/edit`}>Edit</Link>}
    </h1>
  );

  const VideoInfoBlurb = (
    <>
      <PublishedDates firstPublishedDate={publication_date} lastUpdatedDate={updated_at} />
      <p className={classNames(styles.videoSummary, 'mb-4')}>{summary}</p>
    </>
  );

  const scrollToVideo = () => {
    if (videoScrollRef.current) {
      scrollToElementTop(videoScrollRef.current, isMobile ? PRODUCTS_NAV_HEIGHT_APPROX : 77);
    }
  };

  return (
    <section className={styles.page}>
      <ContentWrapper>
        {Title}
        {livestream ? (
          <LivestreamLakeBlurb waterbodies={waterbodies} brand={brands[0]} />
        ) : (
          VideoInfoBlurb
        )}

        <OmniaVideoComponent
          omnia_video={omnia_video}
          omnia_video_items={omniaVideoItems}
          omniaVideoItemsLoadingState={LoadingState.DONE}
          showVideoInfo={false}
          onCurrentVideoItemChange={(value) => setCurrentVideoItemId(value)}
          timeToSeekTo={timeToSeek}
          isDockable={true}
          videoScrollRef={videoScrollRef}
        />
        <MobileVideoLiveStreamReload livestream={livestream} />
        <StreamHasNotStarted omnia_video={omnia_video} />
        <StreamIsOver omnia_video={omnia_video} />
      </ContentWrapper>
      <LakeBanner waterbodies={waterbodies} />

      {(standardOmniaVideo || live || isOver) && productItems.length > 0 && (
        <ContentWrapper>
          <section className={styles.featuredProductsSection}>
            <h2>All Featured Products</h2>
            <ul className={styles.allProductList}>
              {productItems.map((vi, i) => {
                const selected = currentVideoItemId === vi.id;
                const item = (() => {
                  switch (vi.item_type) {
                    case 'ProductFamily':
                      return (
                        <VideoItemProductFamily
                          onJumpClick={() => {
                            setTimeToSeek(vi.start_sec), scrollToVideo();
                          }}
                          omnia_video_item={vi}
                          onClick={omniaVideoOpenProductFamilyModal(
                            vi.item,
                            i,
                            vi,
                            omnia_video,
                            list_uuid
                          )}
                          showJump={standardOmniaVideo}
                        />
                      );
                    case 'Product':
                      return (
                        <VideoItemProduct
                          onJumpClick={() => {
                            setTimeToSeek(vi.start_sec);
                            scrollToVideo();
                          }}
                          omnia_video_item={vi}
                          omnia_video={omnia_video}
                          onAddToCartClick={omniaVideoAddToCart(
                            vi.item,
                            i,
                            vi,
                            omnia_video,
                            list_uuid
                          )}
                          onClick={() => {
                            WebAnalytics.productClick({
                              productOrProductFamily: vi.item,
                              show_modal: false,
                              position: i,
                              list_uuid,
                            });
                            webAnalyticsVideoItemClick(vi, i, omnia_video);
                          }}
                          onOutOfStockClick={() =>
                            dispatch(
                              InventoryModalActions.INVENTORY_MODAL_OPEN(
                                vi.item,
                                WebAnalyticsEventAreas.QUICK_VIEW
                              )
                            )
                          }
                          showJump={standardOmniaVideo}
                        />
                      );
                    default:
                      break;
                  }
                })();
                return (
                  <li
                    className={classNames(styles.allProductsItem, {
                      [styles.current]: isMobile && selected,
                    })}
                    key={vi.id}
                  >
                    {item}
                  </li>
                );
              })}
            </ul>
          </section>
        </ContentWrapper>
      )}
      {livestream && <LivestreamFaqs />}
      {transcript && (
        <section className={styles.transcript}>
          <ContentWrapper>
            <div dangerouslySetInnerHTML={{ __html: transcript }} className={articleStyles.body} />
          </ContentWrapper>
        </section>
      )}
    </section>
  );
};
